import type {
    SmriqAutoMatchSelection,
    SmriqChosenServiceSelection,
    SmriqConfirmedVehicle,
    SmriqLastCompletedServiceSelection,
    SmriqPlanSettings,
} from '@features/service-plans/types/smriq.types';
import { isDateKey } from '../utils/dateKey';

// The vehicle service_plan_session_id was confirmed against. Round-tripped verbatim,
// like payment_selection/schedule_overrides above.
export interface SmriqConfirmedVehiclePayload {
    session_id: string;
    vrm: string;
    current_mileage: number;
    annual_mileage: number;
}

export const toSmriqConfirmedVehiclePayload = (
    vehicle: SmriqConfirmedVehicle | null | undefined,
): SmriqConfirmedVehiclePayload | undefined => {
    if (!vehicle) {
        return undefined;
    }
    return {
        session_id: vehicle.sessionId,
        vrm: vehicle.vrm,
        current_mileage: vehicle.currentMileage,
        annual_mileage: vehicle.annualMileage,
    };
};

export const fromSmriqConfirmedVehiclePayload = (
    payload: SmriqConfirmedVehiclePayload | null | undefined,
): SmriqConfirmedVehicle | undefined => {
    if (!payload) {
        return undefined;
    }
    return {
        sessionId: payload.session_id,
        vrm: payload.vrm,
        currentMileage: payload.current_mileage,
        annualMileage: payload.annual_mileage,
    };
};

// The duration/MOT/last-service/start-date inputs the saved schedule was generated with.
// Already snake_case in-app (maps straight to the generate payload), so the wire shape matches.
export interface SmriqPlanSettingsPayload {
    duration_months: number;
    include_mot_tests: boolean;
    last_completed_service?: SmriqLastCompletedServiceSelection | null;
    plan_start_date?: string;
}

const MAX_SERVICE_OPTION_ID_LENGTH = 191;
const MAX_LABEL_LENGTH = 255;
const MAX_COMPLETED_MILEAGE = 1_000_000;

const isNonEmptyString = (value: unknown): value is string =>
    typeof value === 'string' && value.length > 0;

const hasValidDetails = (candidate: Partial<SmriqChosenServiceSelection>): boolean => {
    const mileage = candidate.completed_mileage;
    return (
        isDateKey(candidate.completed_date) &&
        typeof mileage === 'number' &&
        Number.isInteger(mileage) &&
        mileage >= 0 &&
        mileage <= MAX_COMPLETED_MILEAGE
    );
};

const isValidSelection = (value: unknown): value is SmriqLastCompletedServiceSelection => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    if ('mode' in value) {
        return value.mode === 'auto' && hasValidDetails(value as Partial<SmriqAutoMatchSelection>);
    }
    const candidate = value as Partial<SmriqChosenServiceSelection>;
    return (
        isNonEmptyString(candidate.service_option_id) &&
        candidate.service_option_id.length <= MAX_SERVICE_OPTION_ID_LENGTH &&
        isNonEmptyString(candidate.label) &&
        candidate.label.length <= MAX_LABEL_LENGTH &&
        hasValidDetails(candidate)
    );
};

const copySelection = (
    selection: SmriqLastCompletedServiceSelection,
): SmriqLastCompletedServiceSelection => {
    const details = {
        completed_date: selection.completed_date,
        completed_mileage: selection.completed_mileage,
    };
    if ('mode' in selection) {
        return { mode: 'auto', ...details };
    }
    return { service_option_id: selection.service_option_id, label: selection.label, ...details };
};

// Guarded rather than passed through: the value reaches here off a redux-persist rehydration or
// untrusted wire JSON, so a malformed duration/flag is dropped rather than sent or hydrated.
const isValidPlanSettings = (value: unknown): value is SmriqPlanSettings => {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value as Partial<SmriqPlanSettings>;
    return (
        typeof candidate.duration_months === 'number' &&
        Number.isInteger(candidate.duration_months) &&
        candidate.duration_months > 0 &&
        typeof candidate.include_mot_tests === 'boolean'
    );
};

export const toSmriqPlanSettingsPayload = (
    settings: SmriqPlanSettings | null | undefined,
): SmriqPlanSettingsPayload | undefined => {
    if (!isValidPlanSettings(settings)) {
        return undefined;
    }
    const payload: SmriqPlanSettingsPayload = {
        duration_months: settings.duration_months,
        include_mot_tests: settings.include_mot_tests,
    };
    if (settings.last_completed_service === null) {
        payload.last_completed_service = null;
    } else if (isValidSelection(settings.last_completed_service)) {
        payload.last_completed_service = copySelection(settings.last_completed_service);
    }
    if (isDateKey(settings.plan_start_date)) {
        payload.plan_start_date = settings.plan_start_date;
    }
    return payload;
};

export const fromSmriqPlanSettingsPayload = (
    payload: SmriqPlanSettingsPayload | null | undefined,
): SmriqPlanSettings | undefined => {
    if (!isValidPlanSettings(payload)) {
        return undefined;
    }
    const settings: SmriqPlanSettings = {
        duration_months: payload.duration_months,
        include_mot_tests: payload.include_mot_tests,
    };
    if (isValidSelection(payload.last_completed_service)) {
        settings.last_completed_service = copySelection(payload.last_completed_service);
    }
    if (isDateKey(payload.plan_start_date)) {
        settings.plan_start_date = payload.plan_start_date;
    }
    return settings;
};
